/**
 * useStreamingChat Hook
 *
 * React hooks for managing block-level (segment) chat with streaming support.
 * Provides state management for SSE streaming responses and message history.
 *
 * Key responsibilities:
 * - Fetch and cache segment chat sessions
 * - Stream AI responses token-by-token with SSE
 * - Manage streaming state (loading, content, errors)
 * - Save complete messages after streaming
 * - Handle chat deletion and cache invalidation
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useRef, useMemo } from "react";
import {
  createOrGetSegmentChat,
  getSegmentChat,
  getAllSegmentChats,
  sendMessageStreaming,
  saveAssistantMessage,
  deleteSegmentChat,
} from "../services/segmentChat";
import type {
  SegmentChatCreate,
  SegmentChatMessageCreate,
  SegmentChatMessageResponse,
  SegmentChatResponse,
  StreamChunk,
  StreamingState,
} from "../types/segmentChat";

/**
 * Hook to fetch segment chat for a specific block
 *
 * Fetches the chat session and message history.
 */
export function useSegmentChat(
  transcriptId: string | undefined,
  segmentId: string | undefined,
  enabled = true
) {
  return useQuery<SegmentChatResponse, Error>({
    queryKey: ["segmentChat", transcriptId, segmentId],
    queryFn: () => getSegmentChat(transcriptId!, segmentId!),
    enabled: !!transcriptId && !!segmentId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on 404 (chat doesn't exist yet)
      if (error.message.includes("404")) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Hook to create or get segment chat
 *
 * Creates a new chat session if one doesn't exist.
 */
export function useCreateSegmentChat(transcriptId: string, segmentId: string) {
  const queryClient = useQueryClient();

  return useMutation<SegmentChatResponse, Error, SegmentChatCreate | undefined>(
    {
      mutationFn: (request) =>
        createOrGetSegmentChat(transcriptId, segmentId, request),
      onSuccess: (data) => {
        // Update cache with new chat
        queryClient.setQueryData(
          ["segmentChat", transcriptId, segmentId],
          data
        );
      },
    }
  );
}

/**
 * Hook to delete segment chat
 *
 * Deletes the chat session and all messages.
 */
export function useDeleteSegmentChat(transcriptId: string, segmentId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error>({
    mutationFn: () => deleteSegmentChat(transcriptId, segmentId),
    onSuccess: () => {
      // Invalidate chat query to trigger refetch
      queryClient.invalidateQueries({
        queryKey: ["segmentChat", transcriptId, segmentId],
      });
    },
  });
}

/**
 * Hook for streaming chat functionality
 *
 * Manages the state of streaming responses including content accumulation,
 * token counting, and error handling. Automatically saves the complete
 * assistant message when streaming finishes.
 */
export function useStreamingSegmentChat(
  transcriptId: string,
  segmentId: string
) {
  const queryClient = useQueryClient();
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    content: "",
    error: null,
    promptTokens: null,
    completionTokens: null,
    pendingUserMessage: null,
  });

  // Track abort controller for cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Send a message and stream the response
   */
  const sendMessage = useCallback(
    async (message: SegmentChatMessageCreate) => {
      // Reset state and track pending user message
      setStreamingState({
        isStreaming: true,
        content: "",
        error: null,
        promptTokens: null,
        completionTokens: null,
        pendingUserMessage: message.content,
      });

      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();

      let accumulatedContent = "";
      let promptTokens = 0;
      let completionTokens = 0;

      try {
        // Stream the response
        await sendMessageStreaming(
          transcriptId,
          segmentId,
          message,
          (chunk: StreamChunk) => {
            if (chunk.type === "content") {
              accumulatedContent += chunk.content;
              setStreamingState((prev) => ({
                ...prev,
                content: accumulatedContent,
              }));
            } else if (chunk.type === "complete") {
              promptTokens = chunk.prompt_tokens;
              completionTokens = chunk.completion_tokens;
              setStreamingState((prev) => ({
                ...prev,
                promptTokens,
                completionTokens,
              }));
            } else if (chunk.type === "error") {
              throw new Error(chunk.error);
            }
          }
        );

        // Save the complete assistant message and get the saved message back
        let savedAssistantMessage = null;
        if (accumulatedContent) {
          savedAssistantMessage = await saveAssistantMessage(
            transcriptId,
            segmentId,
            {
              content: accumulatedContent,
              prompt_tokens: promptTokens,
              completion_tokens: completionTokens,
            }
          );
        }

        // Optimistically update the cache with both user and assistant messages
        // This prevents the "disappearing messages" issue during refetch
        queryClient.setQueryData<SegmentChatResponse>(
          ["segmentChat", transcriptId, segmentId],
          (old) => {
            if (!old) return old;

            const now = new Date().toISOString();

            // Create temporary user message (will be replaced by real one on refetch)
            const tempUserMessage: SegmentChatMessageResponse = {
              id: `temp-user-${Date.now()}`,
              chat_id: old.id,
              role: "user",
              content: message.content,
              referenced_segments: null,
              referenced_annotations: null,
              prompt_tokens: null,
              completion_tokens: null,
              message_metadata: null,
              created_at: now,
            };

            const newMessages = [...old.messages, tempUserMessage];

            // Add assistant message if we have one
            if (savedAssistantMessage) {
              newMessages.push(savedAssistantMessage);
            }

            return {
              ...old,
              messages: newMessages,
            };
          }
        );

        // Mark streaming as complete and clear pending user message
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          pendingUserMessage: null,
        }));

        // Also invalidate to get the real user message ID from the server
        // (background refetch, won't cause UI flicker since cache already has data)
        queryClient.invalidateQueries({
          queryKey: ["segmentChat", transcriptId, segmentId],
        });

        // Also invalidate the batch endpoint for segment chat counts
        queryClient.invalidateQueries({
          queryKey: ["segmentChats", transcriptId],
        });
      } catch (error) {
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          pendingUserMessage: null,
          error: error instanceof Error ? error.message : "Streaming failed",
        }));
        throw error;
      } finally {
        abortControllerRef.current = null;
      }
    },
    [transcriptId, segmentId, queryClient]
  );

  /**
   * Cancel ongoing streaming
   */
  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setStreamingState((prev) => ({
        ...prev,
        isStreaming: false,
        error: "Streaming cancelled",
      }));
    }
  }, []);

  /**
   * Reset streaming state
   */
  const resetStreaming = useCallback(() => {
    setStreamingState({
      isStreaming: false,
      content: "",
      error: null,
      promptTokens: null,
      completionTokens: null,
      pendingUserMessage: null,
    });
  }, []);

  return {
    streamingState,
    sendMessage,
    cancelStreaming,
    resetStreaming,
  };
}

/**
 * Hook to fetch message counts for all segments in a transcript
 *
 * Uses batch endpoint to fetch all chats in a single request.
 * More efficient than individual segment requests.
 */
export function useSegmentChatCounts(
  transcriptId: string | undefined,
  segmentIds: string[]
) {
  // Fetch all chats in a single request
  const { data: allChats, isLoading } = useQuery<SegmentChatResponse[], Error>({
    queryKey: ["segmentChats", transcriptId],
    queryFn: () => getAllSegmentChats(transcriptId!),
    enabled: !!transcriptId && segmentIds.length > 0,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Build map of segment ID to message count
  const messageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    if (allChats) {
      allChats.forEach((chat) => {
        if (chat.segment_id && chat.messages) {
          counts[chat.segment_id] = chat.messages.length;
        }
      });
    }
    return counts;
  }, [allChats]);

  return { messageCounts, isLoading };
}
